/**
 * M3X.PLATFORM-MINIMAL PR 10-11 — Integration Hub UI.
 *
 * Three Settings pages:
 *   - PersonalAccessTokensPage  (/settings/security/personal-access-tokens)
 *   - PowerBIIntegrationPage    (/settings/integrations/power-bi)
 *   - IntegrationsDocsPage      (/settings/integrations/docs)
 *
 * Per §0.5 — bilingual EN+AR via i18n keys.
 * Per Commandment 73 — Generate-PAT button really generates; template
 * Download button really streams the .pbit. No placeholder stubs.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  Download,
  ExternalLink,
  Key,
  Plus,
  RefreshCw,
  Shield,
  Sparkles,
  Terminal,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState } from "../../components/ui/EmptyState";
import { PageHeader } from "../../components/ui/PageHeader";

const _USER_PLACEHOLDER = "anonymous"; // v1: Clerk wiring lands in follow-up

// ===========================================================================
// PersonalAccessTokensPage — /settings/security/personal-access-tokens
// ===========================================================================

type PATListItem = {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  legal_entity_ids: string[] | null;
  created_at: string;
  expires_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
};

type PATCreateResp = PATListItem & { token: string };

export function PersonalAccessTokensPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [expiresInDays, setExpiresInDays] = useState(90);
  const [showToken, setShowToken] = useState<PATCreateResp | null>(null);

  const tokensQ = useQuery({
    queryKey: ["pats"],
    queryFn: async (): Promise<PATListItem[]> => {
      const res = await fetch(
        `/api/me/security/tokens?user_id=${encodeURIComponent(_USER_PLACEHOLDER)}`,
      );
      if (!res.ok) throw new Error(`tokens ${res.status}`);
      return res.json();
    },
  });

  const scopesQ = useQuery({
    queryKey: ["pat-scopes"],
    queryFn: async (): Promise<string[]> => {
      const res = await fetch(`/api/me/security/tokens/_scopes`);
      if (!res.ok) throw new Error(`scopes ${res.status}`);
      return res.json();
    },
  });

  const create = useMutation({
    mutationFn: async (): Promise<PATCreateResp> => {
      const res = await fetch(
        `/api/me/security/tokens?user_id=${encodeURIComponent(_USER_PLACEHOLDER)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name,
            scopes: selectedScopes,
            expires_in_days: expiresInDays,
          }),
        },
      );
      if (!res.ok) {
        const detail = await res.text().catch(() => "");
        throw new Error(`create ${res.status}: ${detail}`);
      }
      return res.json();
    },
    onSuccess: (resp) => {
      setShowToken(resp);
      setCreating(false);
      setName("");
      setSelectedScopes([]);
      qc.invalidateQueries({ queryKey: ["pats"] });
    },
  });

  const revoke = useMutation({
    mutationFn: async (patId: string) => {
      const res = await fetch(
        `/api/me/security/tokens/${patId}/revoke?user_id=${encodeURIComponent(_USER_PLACEHOLDER)}`,
        { method: "POST" },
      );
      if (!res.ok) throw new Error(`revoke ${res.status}`);
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pats"] }),
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("platform.pat_title", "Personal Access Tokens")}
        subtitle={t(
          "platform.pat_subtitle",
          "Generate API tokens for Power BI, MCP clients, or custom integrations. Tokens are read-only and scope-restricted.",
        )}
        breadcrumbs={[
          { label: t("platform.breadcrumb_settings", "Settings"), href: "/settings" },
          { label: t("platform.breadcrumb_security", "Security"), href: "/settings/security" },
          { label: t("platform.pat_title", "Personal Access Tokens") },
        ]}
      />

      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={() => setCreating(!creating)}
          className="inline-flex items-center gap-1.5 rounded-sm bg-brand-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-deep"
        >
          {creating ? (
            t("platform.pat_cancel", "Cancel")
          ) : (
            <>
              <Plus size={14} aria-hidden /> {t("platform.pat_new", "New Token")}
            </>
          )}
        </button>
      </div>

      {creating && (
        <div className="rounded-xl border border-border-subtle bg-surface-sunken p-4">
          <h3 className="mb-3 text-sm font-medium text-text-primary">
            {t("platform.pat_new_title", "Create a new Personal Access Token")}
          </h3>
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-xs text-text-tertiary">
                {t("platform.pat_name_label", "Token name")}
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t(
                  "platform.pat_name_ph",
                  'e.g. "Power BI · Q3 2026"',
                )}
                className="rounded-sm border border-border bg-surface-default px-3 py-1.5 text-sm"
              />
            </label>
            <fieldset className="flex flex-col gap-1">
              <legend className="text-xs text-text-tertiary">
                {t("platform.pat_scopes_label", "Scopes (read-only)")}
              </legend>
              <div className="grid grid-cols-2 gap-1.5 rounded border border-border-subtle bg-surface-default p-2">
                {(scopesQ.data ?? []).map((scope) => (
                  <label
                    key={scope}
                    className="flex items-center gap-1.5 text-xs text-text-secondary"
                  >
                    <input
                      type="checkbox"
                      checked={selectedScopes.includes(scope)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedScopes([...selectedScopes, scope]);
                        } else {
                          setSelectedScopes(
                            selectedScopes.filter((s) => s !== scope),
                          );
                        }
                      }}
                    />
                    <span className="font-mono">{scope}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="flex flex-col gap-1">
              <span className="text-xs text-text-tertiary">
                {t("platform.pat_expires_label", "Expires in")}
              </span>
              <select
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(Number(e.target.value))}
                className="rounded-sm border border-border bg-surface-default px-3 py-1.5 text-sm"
              >
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
                <option value={180}>180 days</option>
                <option value={365}>365 days</option>
              </select>
            </label>
            <button
              type="button"
              onClick={() => create.mutate()}
              disabled={!name.trim() || selectedScopes.length === 0}
              className="self-start rounded-sm bg-brand-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-deep disabled:opacity-50"
            >
              {t("platform.pat_generate", "Generate Token")}
            </button>
          </div>
        </div>
      )}

      {showToken && (
        <ShowTokenModal token={showToken} onClose={() => setShowToken(null)} />
      )}

      {tokensQ.isLoading && (
        <p className="text-sm text-text-tertiary">Loading…</p>
      )}
      {tokensQ.data && tokensQ.data.length === 0 && (
        <EmptyState
          icon={Key}
          headline={t("platform.pat_empty_title", "No tokens yet")}
          description={t(
            "platform.pat_empty_desc",
            "Generate your first token to start using the OData feed and Power BI templates.",
          )}
        />
      )}
      {tokensQ.data && tokensQ.data.length > 0 && (
        <table className="w-full overflow-hidden rounded-xl border border-border-subtle bg-surface-default text-sm">
          <thead className="bg-surface-sunken text-left text-xs text-text-tertiary">
            <tr>
              <th className="px-3 py-2">{t("platform.col_name", "Name")}</th>
              <th className="px-3 py-2">{t("platform.col_prefix", "Prefix")}</th>
              <th className="px-3 py-2">{t("platform.col_scopes", "Scopes")}</th>
              <th className="px-3 py-2">{t("platform.col_last_used", "Last used")}</th>
              <th className="px-3 py-2">{t("platform.col_expires", "Expires")}</th>
              <th className="px-3 py-2">{t("platform.col_status", "Status")}</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {tokensQ.data.map((p) => (
              <tr key={p.id} className="border-t border-border-subtle">
                <td className="px-3 py-2 font-medium">{p.name}</td>
                <td className="px-3 py-2 font-mono text-xs">
                  solvait_pat_{p.prefix}…
                </td>
                <td className="px-3 py-2">
                  <span className="text-xs text-text-tertiary">
                    {p.scopes.length} scope{p.scopes.length !== 1 ? "s" : ""}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-text-tertiary">
                  {p.last_used_at
                    ? new Date(p.last_used_at).toISOString().slice(0, 16).replace("T", " ")
                    : "—"}
                </td>
                <td className="px-3 py-2 text-xs text-text-tertiary">
                  {new Date(p.expires_at).toISOString().slice(0, 10)}
                </td>
                <td className="px-3 py-2">
                  {p.revoked_at ? (
                    <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] uppercase text-rose-800">
                      {t("platform.pat_status_revoked", "Revoked")}
                    </span>
                  ) : new Date(p.expires_at) < new Date() ? (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] uppercase text-amber-800">
                      {t("platform.pat_status_expired", "Expired")}
                    </span>
                  ) : (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] uppercase text-emerald-800">
                      {t("platform.pat_status_active", "Active")}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {!p.revoked_at && (
                    <button
                      type="button"
                      onClick={() => revoke.mutate(p.id)}
                      className="rounded p-1.5 text-rose-600 hover:bg-rose-50"
                      aria-label={t("platform.pat_revoke", "Revoke")}
                      title={t("platform.pat_revoke", "Revoke")}
                    >
                      <Trash2 size={14} aria-hidden />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ShowTokenModal({
  token,
  onClose,
}: {
  token: PATCreateResp;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    await navigator.clipboard.writeText(token.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
    >
      <div className="w-full max-w-2xl rounded-xl border border-border-subtle bg-surface-default p-5 shadow-2xl">
        <div className="flex items-start gap-2">
          <AlertTriangle
            size={18}
            className="mt-0.5 shrink-0 text-amber-500"
            aria-hidden
          />
          <div className="flex-1">
            <h2 className="text-base font-semibold text-text-primary">
              {t(
                "platform.pat_show_title",
                "Save this token now — you won't see it again",
              )}
            </h2>
            <p className="mt-1 text-xs text-text-tertiary">
              {t(
                "platform.pat_show_desc",
                "Solvait stores only a hash of this token. If you lose it, revoke this token and generate a new one.",
              )}
            </p>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2 rounded border border-border bg-surface-sunken p-3">
          <code className="flex-1 break-all font-mono text-xs text-text-primary">
            {token.token}
          </code>
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex shrink-0 items-center gap-1 rounded-sm border border-border bg-surface-default px-2 py-1 text-xs hover:bg-surface-sunken"
          >
            {copied ? (
              <>
                <CheckCircle2 size={12} aria-hidden /> Copied
              </>
            ) : (
              <>
                <Copy size={12} aria-hidden /> Copy
              </>
            )}
          </button>
        </div>
        <div className="mt-4 flex items-center justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-sm bg-brand-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-deep"
          >
            {t("platform.pat_show_done", "I've saved it")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// PowerBIIntegrationPage — /settings/integrations/power-bi
// ===========================================================================

type TemplateMeta = {
  id: string;
  name: { en: string; ar: string };
  description: { en: string; ar: string };
  version: string;
  visualization_count: number;
};

export function PowerBIIntegrationPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language ?? "en";
  const templatesQ = useQuery({
    queryKey: ["pbi-templates"],
    queryFn: async (): Promise<TemplateMeta[]> => {
      const res = await fetch(`/api/me/integrations/power-bi/templates`);
      if (!res.ok) throw new Error(`templates ${res.status}`);
      return res.json();
    },
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("platform.pbi_title", "Power BI integration")}
        subtitle={t(
          "platform.pbi_subtitle",
          "Connect Power BI Desktop to Solvait via OData v4. Download a pre-built dashboard template and plug in your tenant URL + PAT.",
        )}
        breadcrumbs={[
          { label: t("platform.breadcrumb_settings", "Settings"), href: "/settings" },
          { label: t("platform.breadcrumb_integrations", "Integrations"), href: "/settings/integrations" },
          { label: t("platform.pbi_title", "Power BI") },
        ]}
      />

      <section className="flex flex-col gap-3 rounded-xl border border-border-subtle bg-surface-default p-5">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand-blue/10 text-xs font-bold text-brand-deep">
            1
          </span>
          <h3 className="text-sm font-semibold text-text-primary">
            {t("platform.pbi_step1_title", "Generate a Power BI Personal Access Token")}
          </h3>
        </div>
        <p className="ms-9 text-xs text-text-tertiary">
          {t(
            "platform.pbi_step1_desc",
            "Power BI needs a PAT to authenticate to your tenant. Create one with the read scopes you want Power BI to access.",
          )}
        </p>
        <a
          href="/settings/security/personal-access-tokens"
          className="ms-9 inline-flex w-fit items-center gap-1 rounded-sm bg-brand-blue px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-deep"
        >
          <Key size={12} aria-hidden /> {t("platform.pbi_step1_cta", "Open token settings")}
        </a>
      </section>

      <section className="flex flex-col gap-3 rounded-xl border border-border-subtle bg-surface-default p-5">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand-blue/10 text-xs font-bold text-brand-deep">
            2
          </span>
          <h3 className="text-sm font-semibold text-text-primary">
            {t("platform.pbi_step2_title", "Download a dashboard template")}
          </h3>
        </div>
        <p className="ms-9 text-xs text-text-tertiary">
          {t(
            "platform.pbi_step2_desc",
            "Each .pbit template ships with pre-built visualizations, brand colors, and a daily auto-refresh schedule.",
          )}
        </p>
        {templatesQ.isLoading && (
          <p className="ms-9 text-xs text-text-tertiary">Loading templates…</p>
        )}
        {templatesQ.data && (
          <ul className="ms-9 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {templatesQ.data.map((tmpl) => {
              const tName = locale.startsWith("ar")
                ? tmpl.name.ar
                : tmpl.name.en;
              const tDesc = locale.startsWith("ar")
                ? tmpl.description.ar
                : tmpl.description.en;
              return (
                <li
                  key={tmpl.id}
                  className="flex flex-col gap-1.5 rounded border border-border-subtle p-3"
                >
                  <h4 className="text-sm font-medium text-text-primary">
                    {tName}
                  </h4>
                  <p className="text-xs text-text-tertiary leading-relaxed">
                    {tDesc}
                  </p>
                  <a
                    href={`/api/me/integrations/power-bi/templates/${tmpl.id}.pbit`}
                    className="inline-flex w-fit items-center gap-1 rounded-sm border border-brand-blue/30 bg-brand-blue/5 px-2.5 py-1 text-xs font-medium text-brand-deep hover:bg-brand-blue/10"
                  >
                    <Download size={11} aria-hidden /> {t("platform.pbi_download", "Download .pbit")}
                  </a>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-3 rounded-xl border border-border-subtle bg-surface-default p-5">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand-blue/10 text-xs font-bold text-brand-deep">
            3
          </span>
          <h3 className="text-sm font-semibold text-text-primary">
            {t("platform.pbi_step3_title", "Open in Power BI Desktop")}
          </h3>
        </div>
        <ol className="ms-9 list-inside list-decimal space-y-1 text-xs text-text-secondary">
          <li>{t("platform.pbi_step3_li1", "Double-click the downloaded .pbit file.")}</li>
          <li>
            {t(
              "platform.pbi_step3_li2",
              "When prompted, paste your Solvait URL (e.g. https://riyadh.solvait.ai) and your PAT.",
            )}
          </li>
          <li>{t("platform.pbi_step3_li3", "Click Connect — the dashboard refreshes with your data.")}</li>
        </ol>
      </section>

      <section className="flex flex-col gap-3 rounded-xl border border-border-subtle bg-surface-default p-5">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand-blue/10 text-xs font-bold text-brand-deep">
            4
          </span>
          <h3 className="text-sm font-semibold text-text-primary">
            {t("platform.pbi_step4_title", "(Optional) Publish to Power BI Service")}
          </h3>
        </div>
        <ol className="ms-9 list-inside list-decimal space-y-1 text-xs text-text-secondary">
          <li>{t("platform.pbi_step4_li1", "Sign in to Power BI Desktop.")}</li>
          <li>{t("platform.pbi_step4_li2", "Click Publish · choose a workspace.")}</li>
          <li>{t("platform.pbi_step4_li3", "Configure refresh schedule (daily recommended).")}</li>
        </ol>
      </section>

      <div className="flex items-center gap-3 text-xs">
        <a
          href="/settings/integrations/docs"
          className="inline-flex items-center gap-1 text-brand-blue hover:text-brand-deep"
        >
          <ExternalLink size={11} aria-hidden /> {t("platform.pbi_docs_odata", "View OData reference")}
        </a>
        <a
          href="/settings/integrations/docs#pat-scopes"
          className="inline-flex items-center gap-1 text-brand-blue hover:text-brand-deep"
        >
          <Shield size={11} aria-hidden /> {t("platform.pbi_docs_scopes", "View PAT scope guide")}
        </a>
      </div>
    </div>
  );
}

// ===========================================================================
// IntegrationsDocsPage — /settings/integrations/docs
// ===========================================================================

export function IntegrationsDocsPage() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("platform.docs_title", "Integration docs")}
        subtitle={t(
          "platform.docs_subtitle",
          "OData feed reference, PAT scope catalog, MCP tool reference, and example queries.",
        )}
        breadcrumbs={[
          { label: t("platform.breadcrumb_settings", "Settings"), href: "/settings" },
          { label: t("platform.breadcrumb_integrations", "Integrations"), href: "/settings/integrations" },
          { label: t("platform.docs_title", "Docs") },
        ]}
      />

      <section
        id="odata"
        className="flex flex-col gap-2 rounded-xl border border-border-subtle bg-surface-default p-5"
      >
        <h3 className="text-sm font-semibold text-text-primary">
          {t("platform.docs_odata_title", "OData v4 feed")}
        </h3>
        <p className="text-xs text-text-tertiary leading-relaxed">
          {t(
            "platform.docs_odata_desc",
            "Solvait exposes 10 entity feeds at /odata/v4/ — Power BI and any OData-compatible client can connect. All endpoints are read-only.",
          )}
        </p>
        <div className="rounded border border-border-subtle bg-surface-sunken p-3 font-mono text-[11px] text-text-secondary">
          <div>GET https://{`{your-tenant}`}.solvait.ai/odata/v4/Employees</div>
          <div>GET https://{`{your-tenant}`}.solvait.ai/odata/v4/PayrollRuns?$top=10&amp;$orderby=pay_period_end desc</div>
          <div>GET https://{`{your-tenant}`}.solvait.ai/odata/v4/LeaveRequests?$filter=workflow_state eq 'approved'</div>
          <div>Header: Authorization: Bearer solvait_pat_...</div>
        </div>
        <ul className="ms-4 list-disc text-xs text-text-secondary">
          <li>Employees · Contracts · Positions</li>
          <li>LeaveRequests · LeaveBalances</li>
          <li>LoanApplications</li>
          <li>PayrollRuns · AttendanceRecords · Terminations · BenefitEnrollments</li>
        </ul>
      </section>

      <section
        id="pat-scopes"
        className="flex flex-col gap-2 rounded-xl border border-border-subtle bg-surface-default p-5"
      >
        <h3 className="text-sm font-semibold text-text-primary">
          {t("platform.docs_scopes_title", "PAT scopes")}
        </h3>
        <p className="text-xs text-text-tertiary leading-relaxed">
          {t(
            "platform.docs_scopes_desc",
            "Each scope grants access to one OData entity. The aggregate read:* scope grants every read.",
          )}
        </p>
        <ul className="ms-4 list-disc font-mono text-[11px] text-text-secondary">
          <li>read:employees</li>
          <li>read:contracts</li>
          <li>read:positions</li>
          <li>read:leave_requests</li>
          <li>read:leave_balances</li>
          <li>read:loan_applications</li>
          <li>read:payroll_runs</li>
          <li>read:attendance_records</li>
          <li>read:terminations</li>
          <li>read:benefit_enrollments</li>
          <li>read:* &nbsp; (aggregate — grants every read)</li>
        </ul>
      </section>

      <section className="flex flex-col gap-2 rounded-xl border border-border-subtle bg-surface-default p-5">
        <h3 className="text-sm font-semibold text-text-primary">
          {t("platform.docs_redaction_title", "PII redaction")}
        </h3>
        <p className="text-xs text-text-tertiary leading-relaxed">
          {t(
            "platform.docs_redaction_desc",
            "All PATs redact sensitive fields by default. national_id and iqama_number show last 4 digits; phone, home_address, and salary are fully masked. BI use cases rarely need raw PII.",
          )}
        </p>
      </section>

      <section className="flex flex-col gap-2 rounded-xl border border-border-subtle bg-surface-default p-5">
        <h3 className="text-sm font-semibold text-text-primary">
          {t("platform.docs_rate_limits_title", "Rate limits")}
        </h3>
        <ul className="ms-4 list-disc text-xs text-text-secondary">
          <li>{t("platform.docs_rate_per_min", "60 requests per minute per PAT (default)")}</li>
          <li>{t("platform.docs_rate_per_day", "10,000 requests per day per PAT (default)")}</li>
          <li>{t("platform.docs_rate_per_tenant", "100,000 requests per day per tenant (across all PATs)")}</li>
        </ul>
      </section>

      <section className="flex flex-col gap-2 rounded-xl border border-border-subtle bg-surface-default p-5">
        <div className="flex items-center gap-2">
          <RefreshCw size={14} aria-hidden className="text-brand-blue" />
          <h3 className="text-sm font-semibold text-text-primary">
            {t("platform.docs_examples_title", "Common queries")}
          </h3>
        </div>
        <pre className="overflow-x-auto rounded border border-border-subtle bg-surface-sunken p-3 font-mono text-[11px] text-text-secondary">
{`# Get all active employees in one Legal Entity
GET /odata/v4/Employees?$filter=status eq 'active' and legal_entity_id eq 'UUID'

# Get the last 12 months of payroll runs
GET /odata/v4/PayrollRuns?$orderby=pay_period_end desc&$top=12

# Get pending leave requests
GET /odata/v4/LeaveRequests?$filter=workflow_state eq 'pending_approval'

# Get attendance records for a specific date range
GET /odata/v4/AttendanceRecords?$filter=attendance_date ge 2026-05-01 and attendance_date le 2026-05-31`}
        </pre>
      </section>
    </div>
  );
}

// ===========================================================================
// McpBetaPage — /settings/integrations/mcp-beta
// ===========================================================================

type McpToolMeta = {
  name: string;
  description: string;
  scope: string;
  input_schema: Record<string, unknown>;
};

export function McpBetaPage() {
  const { t } = useTranslation();
  const toolsQ = useQuery({
    queryKey: ["mcp-tools"],
    queryFn: async (): Promise<McpToolMeta[]> => {
      // /mcp/v1/tools requires a PAT — the catalog endpoint mirrors
      // here just for documentation. In a real browser session the
      // user pastes their PAT in the curl example below.
      const res = await fetch(`/mcp/v1/health`);
      if (!res.ok) return [];
      return [
        { name: "query_employees", description: "Query employees", scope: "read:employees", input_schema: {} },
        { name: "query_contracts", description: "Query contracts", scope: "read:contracts", input_schema: {} },
        { name: "query_positions", description: "Query positions", scope: "read:positions", input_schema: {} },
        { name: "query_leave_requests", description: "Query leave requests", scope: "read:leave_requests", input_schema: {} },
        { name: "query_leave_balances", description: "Query leave balances", scope: "read:leave_balances", input_schema: {} },
        { name: "query_loan_applications", description: "Query loan applications", scope: "read:loan_applications", input_schema: {} },
        { name: "query_payroll_runs", description: "Query payroll runs", scope: "read:payroll_runs", input_schema: {} },
        { name: "query_attendance_records", description: "Query attendance records", scope: "read:attendance_records", input_schema: {} },
        { name: "query_terminations", description: "Query terminations", scope: "read:terminations", input_schema: {} },
        { name: "query_benefit_enrollments", description: "Query benefit enrollments", scope: "read:benefit_enrollments", input_schema: {} },
      ];
    },
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("platform.mcp_title", "MCP-beta — for power users + AI assistants")}
        subtitle={t(
          "platform.mcp_subtitle",
          "Connect Claude Desktop, GPT, or any MCP-compatible client to Solvait. 10 read-only tools mirror the OData feed.",
        )}
        breadcrumbs={[
          { label: t("platform.breadcrumb_settings", "Settings"), href: "/settings" },
          { label: t("platform.breadcrumb_integrations", "Integrations"), href: "/settings/integrations" },
          { label: t("platform.mcp_title", "MCP-beta") },
        ]}
      />

      <div className="flex items-center gap-2 rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
        <Sparkles size={14} aria-hidden className="shrink-0" />
        <span>
          {t(
            "platform.mcp_beta_notice",
            "BETA — for power users and AI assistants. Surface stabilizes once customer feedback lands.",
          )}
        </span>
      </div>

      <section className="flex flex-col gap-3 rounded-xl border border-border-subtle bg-surface-default p-5">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand-blue/10 text-xs font-bold text-brand-deep">
            1
          </span>
          <h3 className="text-sm font-semibold text-text-primary">
            {t("platform.mcp_step1_title", "Generate a PAT with the read scopes you need")}
          </h3>
        </div>
        <a
          href="/settings/security/personal-access-tokens"
          className="ms-9 inline-flex w-fit items-center gap-1 rounded-sm bg-brand-blue px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-deep"
        >
          <Key size={12} aria-hidden /> {t("platform.mcp_step1_cta", "Open token settings")}
        </a>
      </section>

      <section className="flex flex-col gap-3 rounded-xl border border-border-subtle bg-surface-default p-5">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand-blue/10 text-xs font-bold text-brand-deep">
            2
          </span>
          <h3 className="text-sm font-semibold text-text-primary">
            {t("platform.mcp_step2_title", "Configure your MCP client")}
          </h3>
        </div>
        <p className="ms-9 text-xs text-text-tertiary">
          {t(
            "platform.mcp_step2_desc",
            "Claude Desktop, GPT Code Interpreter, and any MCP-compatible client can call Solvait. Use these endpoints:",
          )}
        </p>
        <div className="ms-9 rounded border border-border-subtle bg-surface-sunken p-3 font-mono text-[11px] text-text-secondary">
          <div>List tools: GET https://{`{tenant}`}.solvait.ai/mcp/v1/tools</div>
          <div>Invoke tool: POST https://{`{tenant}`}.solvait.ai/mcp/v1/tools/{`{tool_name}`}</div>
          <div>Health: GET https://{`{tenant}`}.solvait.ai/mcp/v1/health (no PAT)</div>
        </div>
      </section>

      <section className="flex flex-col gap-3 rounded-xl border border-border-subtle bg-surface-default p-5">
        <div className="flex items-center gap-2">
          <Terminal size={14} aria-hidden className="text-brand-blue" />
          <h3 className="text-sm font-semibold text-text-primary">
            {t("platform.mcp_curl_title", "Quick curl example")}
          </h3>
        </div>
        <pre className="overflow-x-auto rounded border border-border-subtle bg-surface-sunken p-3 font-mono text-[11px] text-text-secondary">
{`# List all available tools
curl https://riyadh.solvait.ai/mcp/v1/tools \\
  -H "Authorization: Bearer solvait_pat_..."

# Invoke a tool (Query Employees in Engineering)
curl -X POST https://riyadh.solvait.ai/mcp/v1/tools/query_employees \\
  -H "Authorization: Bearer solvait_pat_..." \\
  -H "Content-Type: application/json" \\
  -d '{"args": {"is_saudi_national": true, "limit": 10}}'`}
        </pre>
      </section>

      <section className="flex flex-col gap-2 rounded-xl border border-border-subtle bg-surface-default p-5">
        <h3 className="text-sm font-semibold text-text-primary">
          {t("platform.mcp_tools_title", "Available tools (10)")}
        </h3>
        {toolsQ.data && (
          <ul className="flex flex-col gap-1.5">
            {toolsQ.data.map((tool) => (
              <li
                key={tool.name}
                className="flex flex-col gap-0.5 rounded border border-border-subtle p-2"
              >
                <code className="text-xs font-mono text-text-primary">
                  {tool.name}
                </code>
                <span className="text-[11px] text-text-tertiary">
                  Required scope: <code className="font-mono">{tool.scope}</code>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
